<script>
import SetDetailsExercise from '@components/exercise/set-details-exercise.vue';
import resize from '@utils/image-sizer';

import {
  exerciseComputed,
  exerciseMethods,
  modalMethods,
} from '@state/helpers';
import { mixin as clickaway } from 'vue-clickaway';
import isEqual from 'lodash/isEqual';

export default {
  components: {
    SetDetailsExercise,
  },
  mixins: [clickaway],
  props: {
    exercise: {
      type: Object,
      required: true,
      default: () => {},
    },
    exerciseIndex: {
      type: Number,
      required: true,
      default: null,
    },
    circuit: {
      type: Object,
      required: false,
      default: () => {},
    },
    selectedExerciseIndex: {
      type: Number,
      required: false,
      default: null,
    },
    overActionFooter: {
      type: Boolean,
      required: true,
    },
    allowEditing: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      placeholder: require('@assets/images/placeholder-photo-upload.svg'),
    };
  },
  computed: {
    ...exerciseComputed,
    isSelected() {
      return (
        isEqual(this.selectedExercise, this.exercise) &&
        this.selectedExerciseIndex === this.exerciseIndex
      );
    },
    hasExerciseMetaData() {
      return (
        (this.exercise &&
          this.exercise.muscleGroups &&
          this.exercise.muscleGroups.length) ||
        (this.exercise.exerciseDifficulty &&
          this.exercise.exerciseDifficulty.exerciseDifficulty_id)
      );
    },
    hasMuscleGroups() {
      return (
        this.exercise &&
        this.exercise.muscleGroups &&
        this.exercise.muscleGroups.length
      );
    },
    hasExerciseSets() {
      return (
        this.exercise &&
        this.exercise.setDirections &&
        this.exercise.setDirections.length
      );
    },
    coverPhoto() {
      return this.exercise &&
        this.exercise.mediaAssets &&
        this.exercise.mediaAssets.coverPhoto &&
        this.exercise.mediaAssets.coverPhoto.url
        ? resize(this.exercise.mediaAssets.coverPhoto.url, 300, 170)
        : this.placeholder;
    },
    originalCover() {
      return this.exercise &&
        this.exercise.mediaAssets &&
        this.exercise.mediaAssets.coverPhoto &&
        this.exercise.mediaAssets.coverPhoto.url
        ? this.exercise.mediaAssets.coverPhoto.url
        : this.placeholder;
    },
  },
  mounted() {},
  methods: {
    ...exerciseMethods,
    ...modalMethods,
    toggleSelectExercise() {
      if (this.allowEditing) {
        if (
          this.selectedExerciseIndex === this.exerciseIndex &&
          isEqual(this.selectedExercise, this.exercise)
        ) {
          this.$emit('set-selected-exercise-index', null);
          this.setSelectedExercise(null);
        } else {
          this.$emit('set-selected-exercise-index', this.exerciseIndex);
          this.setSelectedExercise(this.exercise);
        }
      }
    },
    clickedOutside() {
      if (
        typeof this.selectedExerciseIndex === 'number' &&
        typeof this.exerciseIndex === 'number' &&
        this.selectedExerciseIndex === this.exerciseIndex &&
        !this.overActionFooter
      ) {
        this.$emit('set-selected-exercise-index', null);
        this.setSelectedExercise(null);
      }
    },
    onViewPhoto(src) {
      this.setSelectedImage({ src });
      this.$bvModal.show('fullsize-image-modal');
    },
  },
  watch: {
    exercise: {
      deep: true,
      handler(n, o) {
        if (
          !isEqual(this.selectedExercise, this.exercise) &&
          this.selectedExerciseIndex === this.exerciseIndex
        ) {
          this.$emit('set-selected-exercise-index', null);
          this.setSelectedExercise(null);
        }
      },
    },
    selectedExercise: {
      deep: true,
      handler(n, o) {
        if (!n && o) {
          this.$emit('set-selected-exercise-index', null);
        }
      },
    },
    selectedExerciseIndex(n, o) {
      if (n === this.exerciseIndex) {
        const element = this.$refs.cardCircuit;
        const container = document.getElementsByClassName('content-wrapper')[0];
        container.scroll({
          top: element.offsetTop - window.innerHeight / 2,
          behavior: 'smooth',
        });
      }
    },
  },
};
</script>

<template>
  <div
    class="card-circuit-container"
    ref="cardCircuit"
    v-on-clickaway="clickedOutside"
  >
    <div
      :class="[
        'card-circuit-header',
        { selected: isSelected, 'allow-editing': allowEditing },
      ]"
      @click="toggleSelectExercise"
    >
      <div class="row">
        <div class="col-2">
          <img :src="coverPhoto" @click.stop="onViewPhoto(originalCover)" />
        </div>
        <div class="col-10">
          <div class="card-circuit-title-wrapper">
            <h4
              class="list-item-categories"
              :style="[!hasExerciseMetaData ? { visibility: 'hidden' } : '']"
            >
              <template v-if="hasMuscleGroups">
                <span v-for="(group, i) in exercise.muscleGroups" :key="i">
                  {{ group.label
                  }}<span v-if="i + 1 < exercise.muscleGroups.length">,</span>
                </span>
              </template>
              |
              {{
                exercise &&
                exercise.exerciseDifficulty &&
                exercise.exerciseDifficulty.label
                  ? exercise.exerciseDifficulty.label
                  : null
              }}
            </h4>
            <h2>{{ exercise.label }}</h2>
          </div>
        </div>
      </div>
    </div>
    <div class="card-circuit-content">
      <SetDetailsExercise
        :exercise="exercise"
        :exerciseIndex="exerciseIndex"
        v-if="hasExerciseSets"
        :allowEditing="allowEditing"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.card-circuit-container {
  background-color: white;
  border-radius: $base-border-radius-lg;
  box-shadow: 0 0.4rem 0.8rem rgba($color-black-wo-rgb, 0.08);
  margin-bottom: 1.6rem;
  transition: transform 300ms;
  transition-timing-function: ease-out;
  &:hover {
    box-shadow: 0 0.4rem 0.8rem rgba($color-black-wo-rgb, 0.1);
    transform: scale(1.005);
  }

  .card-circuit-header {
    align-items: center;
    border-bottom: 6px solid $color-card-circuit-header-border;
    display: block;
    min-height: 13.4rem;
    padding: 2.4rem 3.3rem;

    &:hover {
      &.allow-editing {
        cursor: pointer;
      }
    }

    &.selected {
      background-color: $color-card-circuit-header-bg-selected;
      border-bottom-color: $color-card-circuit-border-selected;
      border-top-left-radius: $base-border-radius-lg;
      border-top-right-radius: $base-border-radius-lg;
    }

    img {
      border-radius: $base-border-radius-sm;
      width: 100%;
    }

    .card-circuit-title-wrapper {
      display: flex;
      flex-direction: column;
      height: 100%;
      justify-content: center;

      h4 {
        color: $color-gray;
        font-size: 1.1rem;
        text-transform: uppercase;
      }
    }
  }

  .card-circuit-content {
    min-height: 22.6rem;
    padding: 2.5rem;
  }
}
</style>
